import React, { useEffect } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from '@/components/ui/sidebar';
import { BarChart3, Phone, MessageSquare, Settings, Users2, Shield, Megaphone, TestTube, X, UserCog, Clock, Building2, Receipt, DollarSign, Key, Palette, Globe, Cog } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { NavLink, useLocation } from 'react-router-dom';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { useBranding } from '@/hooks/useBranding';

const menuItems = [
  { title: 'Analytics', icon: BarChart3, url: '/analytics' },
  { title: 'Campaigns', icon: Megaphone, url: '/campaigns' },
  { title: 'Calls', icon: Phone, url: '/calls' },
  { title: 'Messages', icon: MessageSquare, url: '/messages' },
  { title: 'Team', icon: Users2, url: '/team' },
  { title: 'Settings', icon: Settings, url: '/settings' },
];

export function AppSidebar() {
  const { isAdmin, isAgency, loading } = useAuth();
  const { setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { branding } = useBranding();
  const showAdmin = !loading && isAdmin();
  const showAgency = !loading && isAgency();

  // Close mobile sidebar on route change as safety net
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar>
      <SidebarHeader className="p-4 flex flex-row items-center justify-between">
        {branding.logo_url ? (
          <img 
            src={branding.logo_url} 
            alt="Logo" 
            className="h-8 w-auto max-w-[140px] object-contain"
          />
        ) : (
          <h1 className="text-xl font-bold text-primary">Calie.ai</h1>
        )}
        {isMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setOpenMobile(false)}
            className="min-h-[44px] min-w-[44px]"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink 
                      to={item.url} 
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAgency && (
          <SidebarGroup>
            <SidebarGroupLabel>Agency</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/agency/billing"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Receipt className="h-5 w-5" />
                      <span>Client Billing</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/agency/branding"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Palette className="h-5 w-5" />
                      <span>Branding</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/agency/domain"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Globe className="h-5 w-5" />
                      <span>Custom Domain</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/tenants"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Building2 className="h-5 w-5" />
                      <span>Tenant Overview</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/branding"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Palette className="h-5 w-5" />
                      <span>Branding Overview</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink 
                      to="/admin/twilio-subaccounts" 
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Shield className="h-5 w-5" />
                      <span>Twilio Subaccounts</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink 
                      to="/testing" 
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <TestTube className="h-5 w-5" />
                      <span>Testing Suite</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/user-account-types"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <UserCog className="h-5 w-5" />
                      <span>User Account Types</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/sms-rate-limits"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Clock className="h-5 w-5" />
                      <span>SMS Rate Limits</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/billing"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <DollarSign className="h-5 w-5" />
                      <span>Billing</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/security/encryption"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Key className="h-5 w-5" />
                      <span>Encryption Keys</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild className="min-h-[44px]">
                    <NavLink
                      to="/admin/settings"
                      onClick={handleNavClick}
                      className={({ isActive }) => `flex items-center gap-3 ${isActive ? 'text-primary font-medium' : ''}`}
                    >
                      <Cog className="h-5 w-5" />
                      <span>Platform Settings</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
